import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AnalyticEvent, AnalyticsBloc, AnalyticsPageName } from '@kanalytics';
import { TagBloc } from '@kbloc';
import { EnvironmentVariablesService } from '@kenv';
import { AuthenticationBloc } from '@kp/auth/authentication.bloc';
import { DialogueBloc } from '@kp/dialogue/dialogue.bloc';
import { DialogueBlockType } from '@kp/dialogue/models/dialogue-block.model';
import { PulseSurveyBloc } from '@kp/pulse-survey/pulse-survey.bloc';
import { Constants } from '@kp/shared/constants.service';
import { UserBloc } from '@kp/user/user.bloc';
import { AuthDataService, DataStoreService, OnboardingUtilities, TitleService, WINDOW } from '@kservice';
import { Product, UserType } from '@ktypes/enums';
import { AuthData, AuthProviderState, DataStatus, SOCIAL_SSO, Status, User } from '@ktypes/models';
import { Popup } from '@kutil';
import { Observable, Subject, merge, of, timer } from 'rxjs';
import { catchError, debounce, map, skipWhile, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'kp-screen-signup',
  templateUrl: './screen-signup.component.html',
  styleUrls: ['./screen-signup.component.scss'],
})
export class ScreenSignupComponent implements OnInit, OnDestroy {
  loginDisabled: boolean;
  errorMessage: string;
  form: UntypedFormGroup;
  hasError$: Observable<boolean>;
  signUpError = false;
  socialSso = SOCIAL_SSO;

  private _destroy$ = new Subject<void>();
  private _emailControl: UntypedFormControl;
  private _hasAdvanced = false;
  private _hasUpdated = false;
  private _loginError: boolean;
  private _popup: Popup;
  private _resetSignUpStatusSub$ = new Subject<void>();
  private _title = 'Signup';

  constructor(
    private _analyticsBloc: AnalyticsBloc,
    private _authDataService: AuthDataService,
    private _authenticationBloc: AuthenticationBloc,
    private _dataStoreService: DataStoreService,
    private _dialogueBloc: DialogueBloc,
    private _environmentVariablesService: EnvironmentVariablesService,
    private _onboardingUtil: OnboardingUtilities,
    private _popupService: Popup,
    private _pulseSurveyBloc: PulseSurveyBloc,
    private _router: Router,
    private _tagBloc: TagBloc,
    private _titleService: TitleService,
    private _userBloc: UserBloc,
    private _ngZone: NgZone,
    @Inject(WINDOW) private _window: Window
  ) {}

  ngOnInit(): void {
    const ERROR_THROTTLE = 500;
    const VALID_THROTTLE = 0;
    this._titleService.set(this._title);
    this._dataStoreService.resetStatus();

    // Setup form
    this._emailControl = new UntypedFormControl(null, [Validators.required, Validators.pattern(/^.+@.+\..{2,}$/)]);
    this.form = new UntypedFormGroup({ email: this._emailControl });

    // handle email error
    this.hasError$ = this._emailControl.valueChanges.pipe(
      map((/* value */) => {
        // we update on valueChange, but rely on the form validators to actually check validity
        return !this._emailControl.valid;
      }),
      // if there is an error, wait for the throttle time to expire before showing in the UI
      // if the error is cleared, immediately clear the error in the UI
      // new values will cause previous values to be discarded
      debounce((hasError) => timer(hasError ? ERROR_THROTTLE : VALID_THROTTLE))
    );

    const userFetchingDestroy$ = new Subject<void>();
    this._dataStoreService.user$
      .pipe(
        skipWhile((user: User) => user == null),
        take(1),
        switchMap((user: User) => {
          if (this._setEmailFormIfEmailExists(user)) {
            userFetchingDestroy$.next();
            userFetchingDestroy$.complete();
          } else if (user?.id) {
            this._userBloc.getUser(user.id);
            return this._userBloc.fetchingUser$;
          }
        }),
        skipWhile((userFetchingStatus: DataStatus<boolean>) => userFetchingStatus?.data),
        take(1),
        switchMap(() => this._dataStoreService.user$),
        takeUntil(merge(this._destroy$, userFetchingDestroy$))
      )
      .subscribe((user: User) => {
        if (this._setEmailFormIfEmailExists(user)) {
          userFetchingDestroy$.next();
          userFetchingDestroy$.complete();
        }
      });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
    this._resetSignUpStatusSub$.complete();
  }

  emailUpdated() {
    this.signUpError = false;
  }

  onSubmit(value) {
    this._resetSignUpStatusSub$.next(); // clear previous subscription
    this._userBloc.signUpStatus$
      .pipe(
        takeUntil(merge(this._destroy$, this._resetSignUpStatusSub$)),
        catchError((error) => {
          console.warn('error in signup component: ', error);
          return of(error);
        })
      )
      .subscribe((signUpStatus) => {
        if (signUpStatus?.status === Status.done) {
          void this._router.navigate(['welcome/signup/verification/email']);
        } else if (signUpStatus?.status === Status.error) {
          // possible errors:
          // - bad request,
          // - KumanuIdentityServiceError - CodeDeliveryFailureException,
          // - KumanuIdentityServiceError - UsernameExistsException
          this.errorMessage = this._authenticationBloc.getAuthErrorMessage(
            signUpStatus?.message?.message,
            'There was an error attempting to use that email to sign up.'
          );
          this.signUpError = true;
        }
      });

    if (this.form?.valid && value?.email !== '') {
      // clear any existing Payment Required Error Messages, then attempt sign-up request
      this._authenticationBloc.resetPaymentRequiredErrorMessage();
      this._userBloc.signUp(this.form?.value?.email);
    }
  }

  signUpWithGoogle(provider: string) {
    this._analyticsBloc.sendEvent(
      this._analyticsBloc.createEventFromInteraction({
        page: AnalyticsPageName.signup,
        event: AnalyticEvent.google_account_link_attempted,
      })
    );
    this._window['kpUpdateUser'] = this._updateUserNickname.bind(this);
    this._popup = this._popupService.initialize({
      url: 'about:blank',
      name: 'ssosignup',
      windowFeatures: {
        width: 640,
        height: 680,
      },
      onClose: this._onPopupClose.bind(this),
    });
    this._loginError = false;
    this.loginDisabled = true;
    const token = this._dataStoreService.authData?.token || 'state';
    const id = this._dataStoreService.authData?.userId || this._dataStoreService.authData?.user?.id || null;

    // Make sure the user is logged out
    if (this._popup) {
      let url = Constants.ssoLogoutUrlTemplate
        .replace('$authHost', this._environmentVariablesService.config.oauth.domain)
        .replace('$clientId', this._environmentVariablesService.config.oauth.authClientSocialId)
        .replace('$kumanuLogout', encodeURIComponent(this._environmentVariablesService.config.oauth.redirectSignOut));

      // Make sure the user is logged out
      this._popup.change_url(url);

      // setup URL for SSO login
      url = Constants.ssoLoginUrlTemplate
        .replace('$authHost', this._environmentVariablesService.config.oauth.domain)
        .replace('$provider', provider)
        .replace('$kumanuHost', encodeURIComponent(this._environmentVariablesService.config.oauth.redirectSignIn))
        .replace('$response_type', this._environmentVariablesService.config.oauth.responseType)
        .replace('$clientId', this._environmentVariablesService.config.oauth.authClientSocialId)
        .replace(
          '$state',
          new AuthProviderState(provider, token, id, this._environmentVariablesService.product as Product).asBase64
        );

      this._checkPopupUrl(url);
    }

    // Re-enable login buttons after 1s
    setTimeout(() => (this.loginDisabled = false), 1000);
  }

  private _setEmailFormIfEmailExists(user: User): boolean {
    const existingEmail = user?.proposedEmail || user?.subscriptionPlanEmail;
    if (existingEmail) {
      this._emailControl.setValue(existingEmail);
      this.emailUpdated();
    }
    return !!existingEmail;
  }

  private _checkPopupUrl(url: string) {
    // after 4s, head to login regardless
    const failsafeTimer = setTimeout(() => {
      this._popup.replace_url(url);
      this._popup.focus();
    }, 4000);

    if (
      this._popup?.popupWin?.location?.href?.includes(this._environmentVariablesService.config.oauth.redirectSignOut)
    ) {
      clearTimeout(failsafeTimer);
      this._popup.replace_url(url);
      this._popup.focus();
      return;
    }

    // check for url change and close popup
    setTimeout(() => {
      this._checkPopupUrl(url);
    }, 250);
  }

  private _onPopupClose() {
    this._loginError = false;
    this.loginDisabled = false;
  }

  private _updateUserNickname({ token, refreshToken }): Promise<boolean> {
    return new Promise((resolve, reject) => {
      // Save token information to authData
      const authData = new AuthData().deserialize({ token, refreshToken });
      this._authDataService.updateToken(authData);

      this._resetSignUpStatusSub$.next(); // clear previous subscription (likely from email login attempt)
      // setup subscription to listen for login status to be done or error
      this._dataStoreService.authStatus$
        .pipe(takeUntil(merge(this._destroy$, this._resetSignUpStatusSub$)))
        .subscribe((loginStatus) => {
          if (loginStatus != null) {
            if (loginStatus.status === Status.done && loginStatus?.data?.user?.email != null) {
              const user = { ...loginStatus.data.user };
              if (!this._hasUpdated && user?.type === UserType.user) {
                this._hasUpdated = true;
                // on SSO login, need to update the user to save the nickname to the server
                this._userBloc.updateUserNickname(user?.nickname);
              }
              resolve(true);
              this._stopSubscriptionAndAdvance();
            } else if (loginStatus.status === Status.error) {
              reject({ error: 'LOGIN_ERROR', message: 'Error during login' });
            }
          }
        });

      // fire off refresh token request, include user details
      this._userBloc.refreshUserFromToken(reject.bind({ error: 'SIGNUP_ERROR', message: 'Error during signup' }));
    });
  }

  private _stopSubscriptionAndAdvance() {
    if (!this._hasAdvanced) {
      this._resetSignUpStatusSub$.next(); // clear authStatus$ subscription
      // clean up localStorage, global function
      this._onboardingUtil.removeOnboardingData();
      delete this._window['kpUpdateUser'];
      this._hasAdvanced = true;

      // Move to initial app screen
      this._pulseSurveyBloc.allowPulseSurveyCheckToday(true);
      this._tagBloc.getUserTags();
      // Use ngZone because this is outside the global Zone
      this._ngZone.run(() => {
        this._dialogueBloc.currentDialogueBlock$.pipe(takeUntil(this._destroy$)).subscribe((currentDialogueBlock) => {
          if (currentDialogueBlock?.type === DialogueBlockType.dialogue_block_signup_sequence) {
            void this._router.navigate(['dialogue'], { replaceUrl: true });
          } else {
            void this._router.navigate([''], { replaceUrl: true });
          }
        });
      });
    }
  }
}
